import { useMutation, useQueryClient } from "@tanstack/react-query"

import type { WorkflowItem } from "@/api/models"

import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"

function toggleWorkflowFavorite(workflowId: string): Promise<WorkflowItem> {
  return api.patch(`/explorer/workflow/${workflowId}/favorite`)
}

export function useToggleWorkflowFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.toggleWorkflowFavorite,
    mutationFn: toggleWorkflowFavorite,
    onMutate: async (workflowId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.workflows })

      // Snapshot the previous value
      const previousWorkflows = queryClient.getQueryData(queryKeys.workflows)

      // Optimistically update the is_favorite field
      queryClient.setQueryData(
        queryKeys.workflows,
        (old: { workflows: WorkflowItem[] } | undefined) => {
          if (!old) return old
          return {
            ...old,
            workflows: old.workflows.map((wf) =>
              wf.id === workflowId ? { ...wf, is_favorite: !wf.is_favorite } : wf
            ),
          }
        }
      )

      return { previousWorkflows }
    },
    onError: (_err, _workflowId, context) => {
      // Rollback on error
      if (context?.previousWorkflows) {
        queryClient.setQueryData(queryKeys.workflows, context.previousWorkflows)
      }
    },
    onSettled: () => {
      // Invalidate to refetch the latest data
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows })
    },
  })
}
